import type { SignalReading } from '@bloo-team/core-shell'
import type { SourceDevice, Stream } from '@bloo-team/api-client'
import type { Extractor } from '../lib/extractors'
import { matchesFilter } from '../lib/extractors'
import type { DdmStream } from '../lib/data-model'
import { silentMuted, silentStreams, sourceState, streamsWithoutDdm } from '../lib/pipeline-health'
import type { ScopeStreamConfig, StreamConfig } from '../lib/retention'

/**
 * What Datafabric's journey signals DECIDE, with the reads already done
 * (`docs/home-journey-requirements.md` §5, S3). Pure, like `home/home-data.ts`
 * beside it: data in, a `SignalReading` out, so the thresholds are testable
 * without a hook, a query client or a DOM.
 *
 * Where the Datafabric Overview already derives the same fact, these call its
 * derivation rather than forming a second opinion — `sourceState`,
 * `silentStreams`, `streamsWithoutDdm`, `matchesFilter` — so Home cannot
 * disagree with the page it links to.
 *
 * A reading's `value` is the POSITIVE fact ("42 of 51 reporting"), never the
 * defect count: the row is answering "is this done", and a count of what is
 * wrong answers a different question.
 */

const n = (v: number) => v.toLocaleString('en-US')

/** Events are "today so far" — all `daily-stream-details` carries. */
export function fmtEvents(total: number): string {
  if (total >= 1_000_000_000) return `${(total / 1_000_000_000).toFixed(1)}B`
  if (total >= 1_000_000) return `${(total / 1_000_000).toFixed(1)}M`
  if (total >= 1_000) return `${Math.round(total / 1000)}k`
  return String(total)
}

/** Sources reporting: ≥1 device, and inactive no more than a tenth of them. */
export const INACTIVE_BAR = 0.1

export function sourcesReporting(devices: SourceDevice[], total: number, now: number): SignalReading {
  if (total === 0) {
    return {
      state: 'missing',
      value: 'none',
      detail: 'No device is sending to this scope yet. Nothing downstream can be true until one is.',
      action: { label: 'Add sources', to: '/fabric/sources' },
    }
  }
  const inactive = devices.filter((d) => sourceState(d, now) === 'inactive').length
  const reporting = devices.length - inactive
  const share = devices.length === 0 ? 0 : inactive / devices.length
  // The count is over the loaded page and the total is the server's, so a
  // scope with more devices than one page says so rather than implying it
  // counted them all.
  const partialPage = devices.length < total ? ` (first ${n(devices.length)} of ${n(total)})` : ''
  return {
    state: share <= INACTIVE_BAR ? 'healthy' : 'partial',
    value: `${n(reporting)} of ${n(devices.length)} reporting`,
    detail: inactive > 0 ? `${n(inactive)} inactive${partialPage}` : `Every device is reporting${partialPage}`,
    ...(inactive > 0 ? { action: { label: 'Inactive', to: '/fabric/sources', search: { status: 'inactive' } } } : {}),
  }
}

/** Nothing has gone quiet: no reporting device unseen past the stale bar. */
export function sourcesStale(devices: SourceDevice[], now: number): SignalReading {
  if (devices.length === 0) return { state: 'na', value: null }
  const stale = devices.filter((d) => sourceState(d, now) === 'stale').length
  return {
    state: stale === 0 ? 'healthy' : 'partial',
    value: stale === 0 ? 'none stale' : `${n(stale)} not seen in 24h`,
    detail:
      stale === 0
        ? 'Every reporting device has been seen inside a day.'
        : 'A device that stopped without being decommissioned is a gap nobody declared.',
    ...(stale > 0 ? { action: { label: 'Review', to: '/fabric/sources', search: { status: 'stale' } } } : {}),
  }
}

/** Streams carrying events today, and none that went quiet. */
export function streamsLive(streams: Stream[], daily: Record<string, number>, now: number): SignalReading {
  if (streams.length === 0) {
    return {
      state: 'missing',
      value: 'none',
      detail: 'No stream is declared in this scope.',
      action: { label: 'Streams', to: '/fabric/streams' },
    }
  }
  const silent = silentStreams(streams, daily)
  // Before 06:00 "silent today" is not yet news — the same mute the Overview
  // applies, so the two cannot disagree at 05:59.
  const muted = silentMuted(now)
  const live = streams.length - silent.length
  return {
    state: silent.length === 0 || muted ? 'healthy' : 'partial',
    value: `${n(live)} of ${n(streams.length)} carrying events`,
    detail:
      silent.length === 0
        ? 'Every declared stream received data today.'
        : muted
          ? `${n(silent.length)} silent so far — before 06:00 that is not yet news.`
          : `${n(silent.length)} silent today.`,
    ...(silent.length > 0 && !muted
      ? { action: { label: 'Silent', to: '/fabric/streams', search: { filter: 'silent' } } }
      : {}),
  }
}

/** Anything arriving at all, today. */
export function ingestToday(daily: Record<string, number>): SignalReading {
  const total = Object.values(daily).reduce((a, v) => a + (Number.isFinite(v) ? v : 0), 0)
  return {
    state: total > 0 ? 'healthy' : 'missing',
    value: total > 0 ? `${fmtEvents(total)} events today` : 'nothing today',
    detail:
      total > 0 ? 'Today so far — the window the daily read carries.' : 'No event has landed in this scope today.',
    action: { label: 'Streams', to: '/fabric/streams' },
  }
}

/** Extraction is running at all. */
export function extractorsEnabled(list: Extractor[]): SignalReading {
  const enabled = list.filter((e) => matchesFilter(e, 'enabled')).length
  return {
    state: enabled > 0 ? 'healthy' : 'missing',
    value: `${n(enabled)} of ${n(list.length)} enabled`,
    detail:
      enabled > 0
        ? 'Raw events are being parsed into fields.'
        : 'Nothing is parsing: events land raw, and no field-level question can be asked of them.',
    action: {
      label: enabled > 0 ? 'Catalogue' : 'Enable',
      to: '/fabric/extractors',
      search: { filter: enabled > 0 ? 'enabled' : 'disabled' },
    },
  }
}

/** And running cleanly. */
export function extractorsHealthy(list: Extractor[]): SignalReading {
  if (list.length === 0) return { state: 'na', value: null }
  const attention = list.filter((e) => matchesFilter(e, 'attention')).length
  return {
    state: attention === 0 ? 'healthy' : 'partial',
    value: attention === 0 ? 'all healthy' : `${n(attention)} need attention`,
    detail:
      attention === 0
        ? 'No extractor is failing or unpublished.'
        : 'An extractor needing attention drops fields the rest of the console assumes.',
    ...(attention > 0
      ? { action: { label: 'Fix', to: '/fabric/extractors', search: { filter: 'attention' } } }
      : {}),
  }
}

/** Streams carrying data are described in the data model. */
export function streamsModelled(streams: Stream[], ddm: DdmStream[]): SignalReading {
  if (streams.length === 0) return { state: 'na', value: null }
  const missing = streamsWithoutDdm(streams, ddm)
  const modelled = streams.length - missing.length
  return {
    state: missing.length === 0 ? 'healthy' : 'partial',
    value: `${n(modelled)} of ${n(streams.length)} modelled`,
    detail:
      missing.length === 0
        ? 'Every live stream has a data model behind it.'
        : `${n(missing.length)} stream${missing.length === 1 ? '' : 's'} carry data no model describes.`,
    ...(missing.length > 0 ? { action: { label: 'Model', to: '/fabric/data-model', search: { chip: 'missing' } } } : {}),
  }
}

/** Retention is a decision somebody made, not a default nobody saw. */
export function retentionSet(cluster: StreamConfig | null, scope: ScopeStreamConfig): SignalReading {
  // 'forbidden' is a role answer, not a setting: it says this session may not
  // read the scope override, never that none exists.
  const scopeSet = typeof scope === 'object' && scope !== null
  const clusterSet = cluster !== null
  return {
    state: scopeSet || clusterSet ? 'healthy' : 'missing',
    value: scopeSet ? 'set for this scope' : clusterSet ? 'cluster default' : 'unset',
    detail:
      scopeSet || clusterSet
        ? 'How long data is kept is a decision on the record.'
        : 'Nothing states how long data is kept, so nobody can be held to it.',
    action: { label: 'Retention', to: '/fabric/storage', search: { tab: 'retention' } },
  }
}

/** No stream is being kept forever by omission. */
export function storageGoverned(streams: Stream[]): SignalReading {
  if (streams.length === 0) return { state: 'na', value: null }
  const open = streams.filter((s) => s.retention === null).length
  return {
    state: open === 0 ? 'healthy' : 'partial',
    value: open === 0 ? 'every stream bounded' : `${n(open)} without retention`,
    detail:
      open === 0
        ? 'Every stream has a retention its capacity can be planned against.'
        : 'A stream with no retention grows until the disk decides for you.',
    ...(open > 0
      ? { action: { label: 'Set', to: '/fabric/storage', search: { tab: 'capacity', chip: 'no-retention' } } }
      : {}),
  }
}
